import React from 'react';

import {
  Box,
  Button,
  CircularProgress,
  DialogContentText,
} from '@mui/material';

import {
  useDataProvider,
  useNotify,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { useMutation } from '@tanstack/react-query';

import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { IReport } from '../utilities/schemas/report';
import reportStatuses from '../constants/reportStatuses';
import { getReportGenerationFailedMessage } from '../constants/reportMessages';
import hasAccess from '../utilities/hasAccess';
import actions from '../constants/actions';
import { attributesSchema, checkIsTypeAllowed } from '../utilities/reports/permissions';
import { ReportTypes } from '../constants/reportTypes';

const DownloadFileButton = ({
  recordId,
  fileId,
  fileName,
  canDownload,
}: {
  recordId: string;
  fileId: string;
  fileName?: string;
  canDownload: boolean;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const {
    mutate: handleDownloadClick,
    isPending,
  } = useMutation({
    mutationFn: async () => {
      try {
        const response = await dataProvider.getOne('reports/download', { id: `${recordId}/${fileId}` });
        window.open(response.data.downloadUrl, '_blank');
      } catch (e) {
        notify('Error: Could not download report', { type: 'warning' });
      }
    },
  });

  if (!canDownload) return null;

  return (
    <Button
      variant="contained"
      onClick={() => handleDownloadClick()}
      disabled={isPending}
      sx={fileName ? { margin: 2 } : {}}
    >
      <Box gap={2} display="flex" alignItems="center">
        <CloudDownloadIcon />
        {fileName ?? 'Download report'}
      </Box>
    </Button>
  );
};

const DownloadModalReports = ({ reportType } : { reportType: ReportTypes}) => {
  const record = useRecordContext<IReport>();

  const { permissions } = usePermissions();
  const canDownload = hasAccess(permissions, actions.REPORT_DOWNLOAD, {
    schema: attributesSchema,
    check: checkIsTypeAllowed(record?.type ?? reportType),
  });
  const currentReportType = record?.type ?? reportType;

  switch (record?.status) {
    case reportStatuses.LOADING:
      return (
        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <CircularProgress size="1rem" />
          <DialogContentText>
            Generating...
          </DialogContentText>
        </Box>
      );
    case reportStatuses.READY:
      return (
        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <DialogContentText>
            The report is successfully generated
          </DialogContentText>
          {record?.files.map((file) => (
            <DownloadFileButton
              key={file.id}
              recordId={record?.id}
              fileId={file.id}
              fileName={record.files.length === 1 ? undefined : file.fileName}
              canDownload={canDownload}
            />
          ))}
        </Box>
      );
    case reportStatuses.FAILED:
      return (
        <DialogContentText>
          {getReportGenerationFailedMessage(currentReportType)}
        </DialogContentText>
      );
    default:
      return (
        <Button
          type="submit"
          variant="contained"
        >
          Generate report
        </Button>
      );
  }
};

export default DownloadModalReports;
